import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { getEligiblePool } from '../../api/disposalApi';

export default function ToolPickerModal({ siteId, excludeProjectId, onConfirm, onCancel }) {
  const { callerEmail } = useUser();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!siteId || !callerEmail) return;
    setLoading(true);
    getEligiblePool(siteId, callerEmail)
      .then(data => setTools(data.results || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [siteId, callerEmail]);

  function toggleTool(toolId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === tools.length) setSelected(new Set());
    else setSelected(new Set(tools.map(t => t.id || t.tool_id)));
  }

  const totalWeight = useMemo(() => {
    let w = 0;
    for (const t of tools) {
      if (selected.has(t.id || t.tool_id)) w += parseFloat(t.estimated_weight) || 0;
    }
    return w;
  }, [tools, selected]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'var(--color-overlay)', zIndex: 'var(--z-modal)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onCancel}>
      <div style={{
        background: 'var(--color-bg-card)', borderRadius: 'var(--radius-xl)',
        maxWidth: '800px', width: '90vw', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: 'var(--shadow-xl)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-7) var(--space-8)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-heading)' }}>
              Select Tools
            </h2>
            <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Choose eligible tools to assign to this scrap project
            </p>
          </div>
          <button onClick={onCancel} style={{
            background: 'none', border: 'none', fontSize: 'var(--text-xl)',
            color: 'var(--color-text-muted)', cursor: 'pointer', padding: 'var(--space-2)',
            fontFamily: 'var(--font-family)',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-6) var(--space-8)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
              Loading eligible tools…
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-danger)', fontSize: 'var(--text-sm)' }}>
              {error}
            </div>
          ) : tools.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
              No eligible tools at this site. Tools must have approved_scrap = true and supplier status of YES=SUBMIT, NPN, or CAPACITY.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    <input type="checkbox" checked={selected.size === tools.length && tools.length > 0}
                      onChange={toggleAll} />
                  </th>
                  <th style={thStyle}>Tool ID</th>
                  <th style={thStyle}>Alt Tool ID</th>
                  <th style={thStyle}>Tool Type</th>
                  <th style={thStyle}>Supplier Status</th>
                  <th style={thStyle}>Est. Weight (lbs)</th>
                </tr>
              </thead>
              <tbody>
                {tools.map(t => {
                  const tid = t.id || t.tool_id;
                  const isSelected = selected.has(tid);
                  return (
                    <tr key={tid} onClick={() => toggleTool(tid)} style={{
                      cursor: 'pointer', background: isSelected ? 'var(--color-accent-light)' : 'transparent',
                      transition: 'background var(--transition-fast)',
                    }}>
                      <td style={tdStyle}><input type="checkbox" checked={isSelected} readOnly /></td>
                      <td style={tdStyle}>{t.supplier_tool_id || '—'}</td>
                      <td style={tdStyle}>{t.alt_tool_id || '—'}</td>
                      <td style={tdStyle}>{t.tool_type || '—'}</td>
                      <td style={tdStyle}>{t.supplier_status || '—'}</td>
                      <td style={tdStyle}>{t.estimated_weight != null ? Number(t.estimated_weight).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-6) var(--space-8)',
          borderTop: '1px solid var(--color-border)',
        }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {selected.size} tool{selected.size !== 1 ? 's' : ''} selected
            {selected.size > 0 && ` — estimated ${totalWeight.toLocaleString()} lbs total`}
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            <button onClick={onCancel} style={{
              padding: 'var(--space-4) var(--space-8)', border: '1px solid var(--color-border-input)',
              borderRadius: 'var(--radius-md)', background: 'transparent',
              color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-semibold)', fontFamily: 'var(--font-family)', cursor: 'pointer',
            }}>Cancel</button>
            <button
              disabled={selected.size === 0}
              onClick={() => onConfirm([...selected])}
              style={{
                padding: 'var(--space-4) var(--space-8)',
                border: 'none', borderRadius: 'var(--radius-md)',
                background: selected.size === 0 ? 'var(--color-border)' : 'var(--color-accent)',
                color: 'var(--color-on-dark)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)', fontFamily: 'var(--font-family)',
                cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
                opacity: selected.size === 0 ? 0.5 : 1,
              }}
            >
              Assign {selected.size} Tool{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: 'var(--space-3) var(--space-5)',
  fontSize: 'var(--text-2xs)', fontWeight: 'var(--font-semibold)',
  color: 'var(--color-text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.04em', borderBottom: '1px solid var(--color-border)',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: 'var(--space-3) var(--space-5)',
  borderBottom: '1px solid var(--color-border-light)',
  color: 'var(--color-text)', whiteSpace: 'nowrap',
};
